import { useState } from "react";
import { useTranslation } from "react-i18next";
import { ShieldAlert } from "lucide-react";
import {
  EMPTY_INVITATION_FILTERS,
  hasActiveFilters,
} from "@/shared/invitations/build-invitations-query";
import type { InvitationsFilters as Filters } from "@/shared/invitations/build-invitations-query";
import { InvitationsFilters } from "./InvitationsFilters";
import { InvitationsResults } from "@/shared/invitations/InvitationsResults";
import { useInvitationsList } from "@/shared/invitations/use-invitations-list";

/**
 * Invitations across all organizations (ADR-0017). Mount it behind
 * `org.members.read`. Invitee emails are PII: they are rendered, never
 * logged, never sent to analytics or error reporting, never put in the URL
 * (filters live in component state, and there is no email search).
 */
export function InvitationsPage() {
  const { t } = useTranslation();
  const [filters, setFilters] = useState<Filters>(EMPTY_INVITATION_FILTERS);
  const [page, setPage] = useState(1);
  const { data, isLoading, isFetching, isError, error, refetch } = useInvitationsList({
    ...filters,
    page,
  });

  // A narrower filter can strand the table on an empty page: back to page 1.
  const applyFilters = (next: Filters) => {
    setFilters(next);
    setPage(1);
  };

  const filtered = hasActiveFilters(filters);

  return (
    <div data-testid="invitations-screen" className="space-y-6">
      <header className="space-y-2">
        <p className="brand-eyebrow">{t("invitations.eyebrow")}</p>
        <h1 className="text-4xl">{t("invitations.title")}</h1>
        <p className="font-sans text-base font-normal text-muted-foreground">
          {t("invitations.subtitle")}
        </p>
        <p
          data-testid="invitations-pii-note"
          className="flex items-center gap-2 font-sans text-sm text-muted-foreground"
        >
          <ShieldAlert className="h-4 w-4" aria-hidden="true" />
          {t("invitations.piiNote")}
        </p>
      </header>

      <InvitationsFilters
        filters={filters}
        onChange={applyFilters}
        onReset={() => applyFilters(EMPTY_INVITATION_FILTERS)}
      />

      <InvitationsResults
        testIdPrefix="invitations"
        data={data}
        page={page}
        isLoading={isLoading}
        isFetching={isFetching}
        isError={isError}
        error={error}
        onRetry={() => void refetch()}
        onPageChange={setPage}
        emptyTitle={t("invitations.empty.title")}
        emptyBody={filtered ? t("invitations.empty.filtered") : t("invitations.empty.body")}
        pastEndBody={t("invitations.empty.pastEnd")}
      />
    </div>
  );
}
